import {
	DocumentObjectListRefTableEnum,
	MasterInfoEnum,
	PageSize,
	PageYearListEnum,
	UserSettingScreenCodeEnum,
} from '@/constant';
import {
	TCommonUpdateRecommenderBody,
	TCreateCommonAssignmentRequest,
	TCreateCommonPublicAssignmentRequest,
} from '@/stores';
import {
	insertObjectIfV2,
	jsonToUrlParams,
	toCamelCase,
	trimObjectValues,
} from '@/utils';

import { AxiosProgressEvent } from 'axios';
import { omit } from 'ramda';
import { AnyObject } from 'yup';

import api, {
	GetAppUserInfoResponse,
	GetCommonApplicantListResponse,
	GetCommonDocumentObjectListParams,
	GetCommonDocumentObjectListResponse,
	GetCommonInquiryBoardListParams,
	GetCommonNoticeListParams,
	GetDistrictValidatorResponse,
	GetDocumentMappingsBody,
	GetDocumentMappingsResponse,
	GetForeignProgramListResponse,
	GetUpdateHistoryListParams,
	GetWorkerProgramListAllResponse,
	ICommonApplicant,
	ICommonAssignmentApplicant,
	ICommonRecommender,
	IContractedForeignBusiness,
	IFranchiseApplicantToAssignedItem,
	IFranchiseAssignment,
	IFranchiseItem,
	IProgram,
	ISelectApplicantPopup,
	IWorkerProviderLodgingItem,
	TCommonDistrict,
	TCommonGetAppUserInfoBody,
	TCommonGetFieldsBody,
	TCommonGetFieldsResponse,
	TCreateCommonInquiryBoardRequest,
	TCreateCommonNoticeRequest,
	TDocumentUploadDefineItem,
	TDownloadDefineDocumentParams,
	TForeignSystemUnitItem,
	TGetApplicantSalaryAutocompleteApplicantNameResponse,
	TGetAutocompleteManagerNameResponse,
	TGetAutocompleteSuggestionResponse,
	TGetCommonListResponse,
	TGetDataList,
	TGetDocumentByPageParams,
	TGetDocumentDetailResponse,
	TGetForeignSystemUnitListResponse,
	TGetListContractedForeignBusinessParams,
	TGetListUserSettingRequest,
	TGetRelationDocumentInfoParams,
	TRelationDocumentInfo,
	TSaveAnswerCommonInquiryBoard,
	TSystemUnitItem,
	TUpdateDocumentBody,
	TUpdateFieldsRequest,
	TUpdateFieldsResponse,
	TUserSettingBody,
	TYearScreenCode,
	WorkerLodgingItem,
} from '.';

export const verifyEmail = async (email: string) =>
	await api.get('/common/emailVerifier', { params: { email } });

export const verifySubDomain = async (subDomain: string) =>
	await api.get('/system/unit/subDomainVerifier', { params: { subDomain } });

export const uploadFile = async (
	formData: FormData,
	onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
) =>
	await api.post<TDocumentUploadDefineItem[]>(
		'/common/document/upload',
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
			onUploadProgress,
		}
	);

export const commonFileUpload = async (formData: FormData) =>
	await api.post<TDocumentUploadDefineItem[]>('/common/file/upload', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

export const updateDocument = async (body: TUpdateDocumentBody) =>
	await api.post(`/common/document/update`, body);

export const getUserSettingsListFields = async (
	params: TGetListUserSettingRequest
) =>
	api.get('/common/userSettings/listFields', {
		params: trimObjectValues(params),
	});

export const setUserSettings = async (body: TUserSettingBody) =>
	api.post('/common/userSettings', body);

export const updateFields = async (
	data: TUpdateFieldsRequest,
	screenCode?: UserSettingScreenCodeEnum
) =>
	api.post<TUpdateFieldsResponse>(
		'/common/updateFields',
		trimObjectValues(data),
		{
			params: {
				screenCodeForAudit: screenCode,
			},
		}
	);

export const getUpdateHistoryList = async (
	params: GetUpdateHistoryListParams
) =>
	api.get('/common/updateHistory', {
		params: trimObjectValues(params),
	});

export const canAssignApplicant = async (ids: number[]) =>
	await api.post('/common/getPublicFranchiseId', ids);

export const getCommonList = async (
	type: keyof typeof MasterInfoEnum,
	params: AnyObject
) => {
	switch (type) {
		case MasterInfoEnum.DISTRICT: {
			return await api.get<TCommonDistrict>(`/common/district`, {
				params: trimObjectValues(params),
			});
		}
		case MasterInfoEnum.PROGRAM: {
			const { data } = await api.post<TGetDataList<IProgram>>(
				`/common/program/list?allData=true`,
				trimObjectValues(params)
			);
			return { data: data.content };
		}
		case MasterInfoEnum.AUTOCOMPLETE_RECOMMENDER_NAME: {
			return await api.get<string[]>(
				`/worker/recommender/autocompleteRecommenderName`,
				{ params: trimObjectValues(params) }
			);
		}
		case MasterInfoEnum.FRANCHISE:
		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE_NAME:
		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE_EMPLOYER_NAME: {
			return await api.get<IFranchiseItem[]>(`/common/franchise/list`, {
				params: trimObjectValues(params),
			});
		}

		case MasterInfoEnum.RECOMMENDER_FRANCHISE:
			return await api.get<IFranchiseItem[]>(
				'/worker/recommender/franchise/list',
				{
					params: trimObjectValues(params),
				}
			);

		case MasterInfoEnum.FRANCHISE_DROPDOWN: {
			return await api.get<IFranchiseItem[]>(`/common/franchise/dropdownList`, {
				params: trimObjectValues(params),
			});
		}

		case MasterInfoEnum.PUBLIC_FRANCHISE_AGRICULTURE: {
			return await api.get<IFranchiseItem[]>(
				`/common/publicFranchiseAgriculture`,
				{ params: trimObjectValues(params) }
			);
		}
		case MasterInfoEnum.COMMON_APPLICANT: {
			const {
				page = 1,
				pageSize = PageSize.XS,
				allData = false,
				sortSql = '',
				...rest
			} = params;
			const { data } = await api.post<GetCommonApplicantListResponse[]>(
				`/common/applicant/list?${jsonToUrlParams(trimObjectValues({ page, pageSize, allData, sortSql }))}`,
				trimObjectValues(rest)
			);
			return { data: data };
		}

		case MasterInfoEnum.COMMON_ASSIGNMENT_AUTO: {
			const { data } = await api.post<ICommonApplicant[]>(
				`/common/assignment/auto`,
				trimObjectValues(params)
			);
			return { data: data };
		}

		case MasterInfoEnum.ASSIGNMENT_APPLICANT: {
			const { data } = await api.get<TGetDataList<ICommonAssignmentApplicant>>(
				`/common/popupAssignmentApplicant`,
				{
					params: trimObjectValues(params),
				}
			);
			return { data: data?.content, total: data?.total };
		}

		case MasterInfoEnum.UNAUTHORIZED_DEPARTURE_APPLICANT: {
			const { data } = await api.get<TGetDataList<ICommonAssignmentApplicant>>(
				`/common/popupAssignmentApplicant?${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data };
		}

		case MasterInfoEnum.APPLICANT_BY_FRANCHISE: {
			const { data } = await api.get<TGetDataList<ISelectApplicantPopup>>(
				`/common/popupApplicantByFranchise?allData=true&${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data.content };
		}

		case MasterInfoEnum.APPLICANT_BY_PROGRAM: {
			const { data } = await api.get<TGetDataList<ISelectApplicantPopup>>(
				`/common/popup/applicant-list?allData=true&${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data.content };
		}

		case MasterInfoEnum.APPLICANT_BY_LODGING: {
			const { data } = await api.get<TGetDataList<ISelectApplicantPopup>>(
				`/common/popup/applicantLodgingList?${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data };
		}

		case MasterInfoEnum.APPLICANT_BY_RECOMMENDER: {
			const { data } = await api.get<TGetDataList<ISelectApplicantPopup>>(
				`/worker/recommender/${params.id}/popupApplicant?${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data };
		}

		case MasterInfoEnum.COMMON_LODGING: {
			const { data } = await api.get<TGetDataList<WorkerLodgingItem>>(
				`/common/popupLodging?${jsonToUrlParams(params)}`
			);
			return { data: data };
		}

		case MasterInfoEnum.COMMON_POPUP_LODGING: {
			const { data } = await api.get<TGetDataList<WorkerLodgingItem>>(
				`/common/popupLodgingPaging?${jsonToUrlParams(params)}`
			);
			return { data: data?.content, total: data?.total };
		}

		case MasterInfoEnum.COMMON_RECOMMENDER: {
			const { data } = await api.get<TGetDataList<ICommonRecommender>>(
				`/common/popupRecommend?${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data };
		}

		case MasterInfoEnum.WC_FRANCHISE_NEW: {
			const { data } = await api.get<TGetDataList<IFranchiseAssignment>>(
				`/worker/changeWorkPlace/franchiseNew/list?${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data };
		}

		case MasterInfoEnum.WC_FRANCHISE_ASSIGNMENT: {
			const { data } = await api.get<TGetDataList<IFranchiseAssignment>>(
				`/worker/changeWorkPlace/franchiseNew/${params.franchiseId}/assignmentFranchise/list`
			);
			return { data: data };
		}

		case MasterInfoEnum.PROVIDER_LODGING: {
			const { data } = await api.get<TGetDataList<IWorkerProviderLodgingItem>>(
				`/worker/lodging/provider/list?allData=true&${jsonToUrlParams(trimObjectValues(params))}`
			);
			return { data: data.content };
		}

		case MasterInfoEnum.AUTOCOMPLETE_PROVIDER_NAME: {
			const { data } = await api.get<TGetAutocompleteSuggestionResponse>(
				`/common/autocompleteSuggestions`
			);

			return { data: data.lodgings };
		}

		case MasterInfoEnum.AUTOCOMPLETE_ADDRESS: {
			const { data } = await api.get<TGetAutocompleteSuggestionResponse>(
				`/common/autocompleteSuggestions`
			);

			return { data: data.lodgings };
		}

		case MasterInfoEnum.AUTOCOMPLETE_APPLICANT_NAME: {
			const { data } = await api.get<TGetAutocompleteSuggestionResponse>(
				`/common/autocompleteSuggestions`
			);

			return { data: data.applicants };
		}

		case MasterInfoEnum.AUTOCOMPLETE_FRANCHISE: {
			const { data } = await api.get<TGetAutocompleteSuggestionResponse>(
				`/common/autocompleteSuggestions`
			);

			return { data: data.franchises };
		}

		case MasterInfoEnum.AUTOCOMPLETE_RECOMMENDER: {
			const { data } = await api.get<TGetAutocompleteSuggestionResponse>(
				`/common/autocompleteSuggestions`
			);

			return { data: data.recommenders };
		}

		case MasterInfoEnum.AUTO_COMPLETE_MANAGER_NAME: {
			return await api.get<TGetAutocompleteManagerNameResponse>(
				`/system/unit/autocompleteManagerName`
			);
		}

		case MasterInfoEnum.FRANCHISE_APPLICANT_TO_ASSIGNED: {
			const { data } = await api.get<IFranchiseApplicantToAssignedItem>(
				`/common/assigned/franchise/list?applicantId=${params.applicantId}`
			);
			return { data: data };
		}

		case MasterInfoEnum.APPLICANT_SALARY_AUTOCOMPLETE_APPLICANT_NAME: {
			const { data } =
				await api.get<TGetApplicantSalaryAutocompleteApplicantNameResponse>(
					`/worker/applicantSalary/autocompleteApplicantName`
				);

			return { data };
		}

		case MasterInfoEnum.APPLICANT_SALARY_AUTOCOMPLETE_EMPLOYEE_NAME: {
			const { data } =
				await api.get<TGetApplicantSalaryAutocompleteApplicantNameResponse>(
					`/worker/applicantSalary/autocompleteEmployeeName`
				);

			return { data };
		}

		case MasterInfoEnum.PROGRAM_OF_FOREIGN: {
			const { data } = await api.get<IProgram>(
				`/foreign/dashboard/programOfForeign/list`
			);

			return { data };
		}

		case MasterInfoEnum.FOREIGN_DOMESTIC_UNIT: {
			const { data } = await api.get<TGetDataList<IFranchiseItem>>(
				`/foreign/unit/domesticUnitList?allData=true`
			);
			return { data: data.content };
		}

		case MasterInfoEnum.FOREIGN_BUSINESS_UNIT: {
			const searchTypes = [
				{
					searchType: params.searchType?.trim(),
					searchValue: params.searchValue?.trim(),
				},
			];
			const { data } = await api.post<IContractedForeignBusiness[]>(
				`/common/foreignUnit/list`,
				{ searchTypes }
			);
			return { data: data, total: data?.length };
		}

		case MasterInfoEnum.COMMON_TARGET_UNIT: {
			const { data } = await api.get<
				TGetForeignSystemUnitListResponse['content']
			>('/common/inquiryBoardAnswer/targetUnitList');

			return { data };
		}

		case MasterInfoEnum.AUTOCOMPLETE_QUESTIONER: {
			const { data } = await api.get<string[]>(
				'/common/inquiryBoard/autoCompleteQuestioner'
			);

			return { data };
		}

		case MasterInfoEnum.FOREIGN_PROGRAM_LIST: {
			const { data } = await api.post<GetForeignProgramListResponse>(
				`/foreign/program/list?allData=true`
			);

			return { data: data.content };
		}

		case MasterInfoEnum.DOMESTIC_UNIT_DROPDOWN: {
			const { data } = await api.get<TForeignSystemUnitItem>(
				'/foreign/unit/domesticUnitDropdown'
			);
			return { data };
		}

		case MasterInfoEnum.COMMON_NOTICE_RECEIVE_REGISTER_UNIT: {
			const { data } = await api.get<TForeignSystemUnitItem>(
				'/common/noticeReceive/registerUnitList'
			);

			return { data };
		}

		case MasterInfoEnum.COMMON_UNIT_DOMESTIC_REGISTER_LIST: {
			const { data } = await api.get<TSystemUnitItem>(
				'/common/unit/domesticRegisterList'
			);

			return { data };
		}

		case MasterInfoEnum.COMMON_DISTRICT_VALIDATOR: {
			const { data } = await api.post<GetDistrictValidatorResponse>(
				`/common/assignment/district/validator`,
				trimObjectValues(params)
			);
			return { data: [data || []] };
		}

		case MasterInfoEnum.PUBLIC_FRANCHISE_EMPLOYER_NAME: {
			const { data } = await api.get<IFranchiseItem[]>(
				`/common/franchiseAssignmentAutocomplete`,
				{
					params: trimObjectValues(params),
				}
			);
			return { data };
		}

		case MasterInfoEnum.USER_AUTOCOMPLETE_SUGGESTIONS: {
			const { data } = await api.get<{ id: number; name: string }[]>(
				`/common/user/autocompleteSuggestions`
			);
			return { data };
		}

		case MasterInfoEnum.DEVICE_LOG_ADMIN_SUGGESTIONS: {
			// Device-log filter: only admins that actually appear in
			// device_access_log (excludes UNREGISTERED rows where admin_id is null
			// and admins with no log entries). Backed by a dedicated endpoint
			// instead of the generic /common/user/autocompleteSuggestions.
			const { data } = await api.get<{ id: number; value: string }[]>(
				`/worker/device/log/admin/autocompleteSuggestions`
			);
			return { data };
		}

		default:
			return await api.get<TGetCommonListResponse>(
				`/common/${toCamelCase(type)}/list`,
				{ params: trimObjectValues(params) }
			);
	}
};

export const getDocumentDownload = async (params: TGetDocumentByPageParams) =>
	await api.get('/common/document', { params });

export const defineDocumentDownload = async ({
	path,
	folder,
	fileName,
	...params
}: TDownloadDefineDocumentParams) =>
	await api.post(
		`/common/document/download/${path}`,
		{
			params: {
				folder,
				fileName,
			},
		},
		{
			responseType: 'blob',
			params,
		}
	);

export const commonFileDownload = async (
	params: TDownloadDefineDocumentParams
) =>
	await api.post(
		`/common/file/download/${params.path}`,
		{ params },
		{ responseType: 'blob' }
	);

export const defineDocumentAuditDownload = async (
	params: TDownloadDefineDocumentParams
) => await api.get(`/common/document/auditDownload`, { params });

export const defineDocumentDownloadById = async ({
	path,
	folder,
	fileName,
	...params
}: TDownloadDefineDocumentParams) =>
	await api.post(
		`/common/document/download/byId/${path}`,
		{
			params: {
				folder,
				fileName,
			},
		},
		{
			responseType: 'blob',
			params,
		}
	);

export const getAssignmentHistoryLayerList = async (franchiseId: number) =>
	await api.get('/common/assignment/franchise', { params: { franchiseId } });

export const getPublicWorkHistoryLayerList = async (applicantId: number) =>
	await api.get('/common/assignment/workHistory', {
		params: { applicantId, type: 'PUBLIC' },
	});

export const getAssignmentWorkHistoryLayerList = async (applicantId: number) =>
	await api.get('/common/assignment/workHistory', { params: { applicantId } });

export const getYearRegisterList = async (screenCode: TYearScreenCode) => {
	switch (screenCode) {
		case PageYearListEnum.BM1:
			return await api.get('/worker/unauthorizedDeparture/yearRegisterList');
		case PageYearListEnum.EM1:
			return await api.get('/worker/worker/extensionStay/yearRegisterList');
		case PageYearListEnum.PP1:
			return await api.get('/worker/applicantSalary/yearRegisterList');
		case PageYearListEnum.PM1_PROGRAM: //TODO: change this when api update
			return await api.get('/common/yearRegisterList', {
				params: { screenCode: 'PM1' },
			});
		default:
			return await api.get('/common/yearRegisterList', {
				params: { screenCode },
			});
	}
};

export const getDocumentObjectList = async (
	params: GetCommonDocumentObjectListParams
) => {
	switch (params.refTableName) {
		case DocumentObjectListRefTableEnum.APPLICANT: {
			const { data } = await api.post(`/worker/applicant/list?allData=true`, {
				searchTypes: trimObjectValues(params.searchTypes) || [],
			});

			return { data: data.content };
		}
		case DocumentObjectListRefTableEnum.FRANCHISE: {
			const { data } = await api.post(`/worker/franchise/list?allData=true`, {
				searchTypes: trimObjectValues(params.searchTypes) || [],
			});

			return { data: data.content };
		}
		case DocumentObjectListRefTableEnum.LODGING: {
			const { data } = await api.post(`/worker/lodging/list?allData=true`, {
				searchTypes: trimObjectValues(params.searchTypes) || [],
			});

			return { data: data.content };
		}
		case DocumentObjectListRefTableEnum.RECOMMENDER: {
			const { data } = await api.post(`/worker/recommender/list?allData=true`, {
				searchTypes: trimObjectValues(params.searchTypes) || [],
			});

			return { data: data.content };
		}
		case DocumentObjectListRefTableEnum.PROGRAM: {
			const { data } = await api.post(`/worker/program/list?allData=true`, {
				searchTypes: trimObjectValues(params.searchTypes) || [],
			});

			return { data: data.content };
		}

		default: {
			return await api.get<GetCommonDocumentObjectListResponse>(
				`/common/document/documentObjectList?${jsonToUrlParams({
					searchType: String(params.searchType),
					searchValue: String(params.searchValue),
				})}`
			);
		}
	}
};

export const getAutocompleteSuggestion = async () =>
	await api.get<TGetAutocompleteSuggestionResponse>(
		'/common/autocompleteSuggestions'
	);

export const createCommonAssignment = async (
	data: TCreateCommonAssignmentRequest
) => await api.post('/common/assignment', data);

export const createCommonPublicAssignment = async (
	data: TCreateCommonPublicAssignmentRequest,
	deleteIds: number[]
) =>
	await api.post('/common/assignment/public', {
		publicAssignment: data,
		...insertObjectIfV2(deleteIds?.length, {
			deleteIds,
		}),
	});

export const updateCommonRecommender = async (
	data: TCommonUpdateRecommenderBody
) => await api.post('/common/popupRecommend', data);

export const getWorkerProgramAllList = async () =>
	await api.get<GetWorkerProgramListAllResponse>(`/common/programAll/list`);

export const getFields = async (body: TCommonGetFieldsBody) =>
	await api.post<TCommonGetFieldsResponse>(`/common/getFields`, body);

export const replacementFile = async (formData: FormData) =>
	await api.post<TDocumentUploadDefineItem[]>(
		'/common/document/replacementUpload',
		formData,
		{
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}
	);

export const getDocumentDetail = async (id: number) =>
	await api.get<TGetDocumentDetailResponse>(`/common/document/${id}/detail`);

export const replacementForm = async (formData: FormData) =>
	await api.post(`/common/document/replacement/form`, formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

export const downloadIssueApplication = async (
	formData: FormData,
	downloadReason: string,
	downloadFileName: string
) =>
	await api.post('/common/document/convert', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
		responseType: 'blob',
		params: {
			downloadReason,
			downloadFileName,
		},
	});

/* ---- COMMON NOTICE START---- */
export const createCommonNotice = async (data: TCreateCommonNoticeRequest) =>
	await api.post('/common/notice/create', {
		...data,
		...trimObjectValues(omit(['content'], data)),
	});
export const getCommonNoticeList = async ({
	page,
	pageSize,
	sortSql = '',
	...rest
}: GetCommonNoticeListParams) =>
	await api.post(
		`/common/notice/list?${jsonToUrlParams({ page, pageSize, sortSql })}`,
		rest
	);
export const deleteCommonNotice = async (ids: number[]) =>
	await api.post('/common/notice/delete', ids);
export const getCommonNoticeDetail = async (id: string) =>
	await api.get(`/common/notice/detail?id=${id}`);
/* ---- COMMON NOTICE END---- */

/* ---- COMMON NOTICE RECEIVE START---- */
export const getCommonNoticeReceiveList = async ({
	page,
	pageSize,
	sortSql = '',
	...rest
}: GetCommonNoticeListParams) =>
	await api.post(
		`/common/noticeReceive/list?${jsonToUrlParams({ page, pageSize, sortSql })}`,
		rest
	);
export const deleteCommonNoticeReceive = async (ids: number[]) =>
	await api.post('/common/noticeReceive/delete', ids);
export const getCommonNoticeReceiveDetail = async (id: string) =>
	await api.get(`/common/noticeReceive/detail?id=${id}`);
/* ---- COMMON NOTICE RECEIVE END---- */

/* ---- COMMON INQUIRY BOARD START---- */
export const getCommonInquiryBoardList = async ({
	page,
	pageSize,
	allData = false,
	sortSql = '',
	...rest
}: GetCommonInquiryBoardListParams) =>
	await api.post(
		`/common/inquiryBoard/list?${jsonToUrlParams({
			page,
			pageSize,
			allData,
			sortSql,
		})}`,
		rest
	);

export const getCommonInquiryBoardDetail = async (id: number) =>
	await api.get(`/common/inquiryBoard/detail?id=${id}`);

export const getCommonInquiryBoardDetailAnswer = async (id: number) =>
	await api.get(`/common/inquiryBoard/detail/answer?id=${id}`);

export const createCommonInquiryBoard = async (
	data: TCreateCommonInquiryBoardRequest
) => await api.post('/common/inquiryBoard/create', data);

export const deleteCommonInquiryBoard = async (ids: number[]) =>
	await api.post('/common/inquiryBoard/delete', ids);
/* ---- COMMON INQUIRY BOARD END---- */

/* ---- COMMON INQUIRY BOARD ANSWER START---- */
export const getCommonInquiryBoardAnswerList = async ({
	page,
	pageSize,
	allData = false,
	sortSql = '',
	...rest
}: GetCommonInquiryBoardListParams) =>
	await api.post(
		`/common/inquiryBoardAnswer/list?${jsonToUrlParams({
			page,
			pageSize,
			allData,
			sortSql,
		})}`,
		rest
	);

export const getCommonInquiryBoardAnswerDetail = async (id: number) =>
	await api.get(`/common/inquiryBoardAnswer/detail?id=${id}`);

export const getCommonInquiryBoardAnswerDetailAnswer = async (id: number) =>
	await api.get(`/common/inquiryBoardAnswer/detail/answer?id=${id}`);

export const getCommonInquiryBoardAnswerTargetUnitList = async () =>
	await api.get('/common/inquiryBoardAnswer/targetUnitList');

export const deleteCommonInquiryBoardAnswer = async (ids: number[]) =>
	await api.post('/common/inquiryBoardAnswer/delete', ids);

export const saveAnswerCommonInquiryBoard = async ({
	id,
	...data
}: TSaveAnswerCommonInquiryBoard) =>
	await api.post(
		`/common/inquiryBoardAnswer/${id}/saveAnswer`,
		trimObjectValues(data)
	);

export const deleteAnswerCommonInquiryBoard = async (id: number) =>
	await api.post(`/common/inquiryBoardAnswer/${id}/deleteAnswer`);
/* ---- COMMON INQUIRY BOARD ANSWER END---- */

export const getContractedForeignBusiness = async (
	params: TGetListContractedForeignBusinessParams
) => {
	const { id, ...rest } = params;
	return await api.get(
		`/common/unit/${id}/contract/list?${jsonToUrlParams(rest)}`
	);
};

export const resetDefaultPassword = async (id: number) =>
	await api.post('/worker/user/resetDefaultPassword', id);

export const unWithdrawn = async (id: number) =>
	await api.post('/worker/user/unWithdrawn', id);

export const getAppUserInfo = async ({ id, type }: TCommonGetAppUserInfoBody) =>
	await api.get<GetAppUserInfoResponse>('/worker/user/appUserInfo', {
		params: { id, type },
	});

export const getRelationDocumentInfo = async (
	params: TGetRelationDocumentInfoParams
) =>
	await api.get<TRelationDocumentInfo>(`/common/dataDocumentInfo`, { params });

export const getDocumentMappings = async (body: GetDocumentMappingsBody) =>
	await api.post<GetDocumentMappingsResponse>(
		`/common/document/mappings`,
		body
	);

export const combineDocuments = async ({
	downloadReason,
	downloadFileName,
	...rest
}: {
	documentName: string;
	ids: number[];
	downloadReason: string;
	downloadFileName: string;
}) => {
	const response = await api.post('/common/document/combine', rest, {
		responseType: 'blob',
		params: {
			downloadReason,
			downloadFileName,
		},
	});
	return response;
};
